import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Brain, TrendingUp, Shield, Users, Lightbulb, BarChart3 } from 'lucide-react';

const AIConsulting = () => {
  const services = [
    {
      icon: Brain,
      title: 'AI Strategy Development',
      description: 'Comprehensive AI roadmaps aligned with your business objectives. We identify opportunities, assess readiness, and create actionable implementation plans.',
    },
    {
      icon: TrendingUp,
      title: 'Process Optimisation',
      description: 'Transform workflows with intelligent automation. We analyse your operations and deploy AI solutions that reduce costs and increase efficiency.',
    },
    {
      icon: Shield,
      title: 'AI Governance & Ethics',
      description: 'Navigate the complexities of AI implementation responsibly. We ensure your AI initiatives meet regulatory requirements and ethical standards.',
    },
    {
      icon: Users,
      title: 'Team Training & Enablement',
      description: 'Upskill your workforce for the AI era. Custom training programmes that empower your teams to leverage AI tools effectively.',
    },
    {
      icon: Lightbulb,
      title: 'Innovation Workshops',
      description: 'Interactive sessions that spark AI-driven innovation. We facilitate ideation and prototyping to discover breakthrough opportunities.',
    },
    {
      icon: BarChart3,
      title: 'Performance Analytics',
      description: 'Measure and optimise AI impact. We implement dashboards and KPIs that track ROI and guide continuous improvement.',
    },
  ];

  return (
    <div data-testid="ai-consulting-page">
      {/* Hero */}
      <section className="min-h-[70vh] flex items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-elate-surface" />
        <motion.div
          className="absolute top-1/3 left-0 w-96 h-96 bg-elate-yellow/5 rounded-full blur-3xl"
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 10, repeat: Infinity }}
        />

        <div className="container-custom relative z-10 pt-32 pb-20">
          <motion.p
            className="caption text-elate-yellow mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            AI Consulting
          </motion.p>

          <motion.h1
            className="text-headline text-white mb-8 max-w-4xl"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Strategic AI That
            <br />
            <span className="text-elate-yellow">Transforms Business</span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-gray-400 max-w-2xl mb-10 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            We saw the AI revolution coming and pivoted our entire focus. Now we help organisations navigate this landscape with clarity—from strategy to implementation, we're your partner in staying ahead of the curve.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="consulting-cta">
              Book a Consultation
              <ArrowUpRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="caption text-elate-yellow mb-4">Our Services</p>
            <h2 className="text-subhead text-white">
              How We Help
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="card-elate p-8"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <service.icon className="w-10 h-10 text-elate-yellow mb-6" />
                <h3 className="text-xl font-semibold text-white mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-padding bg-elate-surface">
        <div className="container-custom">
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="caption text-elate-yellow mb-4">Our Process</p>
            <h2 className="text-subhead text-white">
              From Vision to Reality
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {['Discover', 'Strategy', 'Implement', 'Scale'].map((step, index) => (
              <motion.div
                key={step}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <p className="text-6xl font-heading text-elate-yellow mb-4">
                  0{index + 1}
                </p>
                <h4 className="text-xl font-semibold text-white mb-2 uppercase tracking-wider">
                  {step}
                </h4>
                <div className="h-0.5 w-16 bg-elate-yellow mx-auto" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            className="bg-elate-yellow p-12 md:p-20 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-subhead text-elate-black mb-6">
              Ready to Lead with AI?
            </h2>
            <p className="text-elate-black/70 max-w-xl mx-auto mb-8 text-lg">
              Let's discuss how AI can transform your organisation. Book a free consultation to explore the possibilities.
            </p>
            <Link
              to="/contact"
              className="bg-elate-black text-white font-bold uppercase tracking-wider px-8 py-4 inline-flex items-center gap-2 hover:bg-white hover:text-elate-black transition-colors"
            >
              Get Started
              <ArrowUpRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AIConsulting;
